import React, { useEffect } from "react";
import { Switch, Redirect, Route, Router } from "react-router-dom";
import { createBrowserHistory } from "history";
import { IntlProvider } from "react-intl";
import NewAnalysis from "./pages/NewAnalysis";
import Login from "./pages/Login";
import AddFunds from "./pages/AddFunds";
import PaymentSuccess from "./pages/PaymentSuccess";
import PrivateRoute from "./components/PrivateRoute";
import PublicRoute from "./components/PublicRoute";
import Template from "./components/Template";
import SignUp from "./pages/SignUp";
import ForgotPassword from "./pages/ForgotPassword";
import ValidateEmail from "./pages/ValidateEmail";
import NotFound from "./pages/NotFound";
import Results from "./pages/Results";
import History from "./pages/History";
import Dashboard from "./pages/Dashboard";
import CompanyReports from "./pages/CompanyReports";
import messages from "./i18n/messages";
import UserContext from "./context/UserContext";
import AppProvider from './hooks';
import About from "./pages/About";
import Contact from "./pages/Contact";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfUse from "./pages/TermsOfUse";
import FAQ from "./pages/FAQ";
import Manual from "./pages/Manual";
import { trackPageView, trackChangeLanguage } from "./utils/analytics";

import { getBalance } from "./api";

const storedLang = localStorage.getItem("stored_lang");
const defaultLang = "pt-BR";

const isTestEnv = process.env.NODE_ENV === "test";

export const history = createBrowserHistory({
  basename: isTestEnv ? "" : "/app"
});

history.listen(location => {
  if (!location.hash) {
    trackPageView(window.location.pathname);
  }
});

const mountedApp = document.getElementById("root");
if (mountedApp) {
  const path = (/#!(\/.*)$/.exec(window.location.hash) || [])[1];
  if (path) {
    history.replace(path);
  }
}

const Routes = () => {
  let [balance, setBalance] = React.useState(0);

  const [lang, setLang] = React.useState(
    !!messages[storedLang] ? storedLang : defaultLang
  );

  function storeLang(newLang) {
    trackChangeLanguage(newLang);
    localStorage.setItem("stored_lang", newLang);
    setLang(newLang);
  }

  function addToBalanceInCents(valueInCents) {
    setBalance(balance => {
      return parseInt(balance, 10) + parseInt(valueInCents, 10);
    });
  }

  const [isUserLoggedIn, setIsUserLoggedIn] = React.useState(false);

  useEffect(() => {
    if (!localStorage.getItem("access_token")) {
      return;
    }

    updateBalance();

    // This runs before component is unmounted
    return function cleanUp() {
      // Fixes issue where previous balance
      // was adding up after sign in
      setBalance(0);
    };
  }, [isUserLoggedIn]);

  function setLoggedIn() {
    setIsUserLoggedIn(true);
  }

  function updateBalance() {
    getBalance().then(response => {
      const amountInteger = response.data.saldo;
      const amountInCents = parseInt(amountInteger, 10) * 100;
      setBalance(amountInCents);
    });
  }

  return (
    <IntlProvider locale={lang} messages={messages[lang]}>
      <Router history={history}>
        <UserContext.Provider
          value={{
            balance,
            addToBalanceInCents,
            setLoggedIn,
            updateBalance,
            storeLang
          }}
        >
          <AppProvider>
            <Switch>
              <PublicRoute path="/login" component={Login} />
              <PublicRoute path="/signup" component={SignUp} />
              <PublicRoute
                path="/forgot_password/:token?"
                component={ForgotPassword}
              />
              <PublicRoute
                path="/validate_email/:token"
                component={ValidateEmail}
              />
              <PrivateRoute path="/new-analysis" component={NewAnalysis} />
              <PrivateRoute path="/results/:id" component={Results} />
              <PrivateRoute path="/add-funds" component={AddFunds} />
              <PrivateRoute path="/payment-success" component={PaymentSuccess} />
              <PrivateRoute path="/history" component={History} />
              <PrivateRoute path="/dashboard" component={Dashboard} />
              <PrivateRoute path="/company-reports" component={CompanyReports} />
              <PrivateRoute path="/about" component={About} />
              <PrivateRoute path="/contact" component={Contact} />
              <Redirect exact from="/" to="/new-analysis" />
              <Route
                path="/privacy-policy"
                render={() => <Template component={PrivacyPolicy} />}
              />
              <Route
                path="/terms-of-use"
                render={() => <Template component={TermsOfUse} />}
              />
              <Route
                path="/manual"
                render={() => <Template component={Manual} />}
              />
              <Route path="/faq" render={() => <Template component={FAQ} />} />
              <Route component={NotFound} />
            </Switch>
          </AppProvider>
        </UserContext.Provider>
      </Router>
    </IntlProvider>
  );
};

export default Routes;
